import {
  KEY_REQUEST_ACTION,
  KEY_REQUEST_DURATION_DEFAULT,
  KEY_REQUEST_SCOPE,
  KEY_REQUEST_STATUS,
  KEY_REQUEST_STATUS_LABELS,
  normalizeMobileNumber
} from '../config/keyRequest';
import { formatDataverseRecordKey } from '../lib/dataverseRecordId';

const CREATED_BY_FORMATTED = '_createdby_value@OData.Community.Display.V1.FormattedValue';

const KEY_REQUEST_SELECT =
  'oc_keyrequestid,oc_name,oc_action,oc_scope,oc_mobilnummer,oc_varighetminutter,oc_status,oc_errormessage,oc_lockscount,oc_completedon,createdon,_oc_lock_value,_oc_innbygger_value';
const KEY_REQUEST_SELECT_WITH_RESULT = `${KEY_REQUEST_SELECT},oc_resultjson`;

function mapKeyRequest(record) {
  const status = record.oc_status;
  return {
    id: record.oc_keyrequestid,
    requestId: record.oc_keyrequestid,
    name: record.oc_name,
    action: record.oc_action,
    scope: record.oc_scope,
    mobileNumber: record.oc_mobilnummer,
    durationMinutes: record.oc_varighetminutter,
    status,
    statusLabel: KEY_REQUEST_STATUS_LABELS[status] || String(status),
    errorMessage: record.oc_errormessage || null,
    locksCount: record.oc_lockscount ?? null,
    completedOn: record.oc_completedon || null,
    createdOn: record.createdon || null,
    resultJson: record.oc_resultjson || null,
    lockId: record._oc_lock_value || null,
    residentId: record._oc_innbygger_value || null,
    createdBy: record.createdby?.fullname || record[CREATED_BY_FORMATTED] || null
  };
}

function buildRequestName({ action, mobileNumber, contextLabel }) {
  const verb =
    action === KEY_REQUEST_ACTION.REVOKE
      ? 'Revoke'
      : action === KEY_REQUEST_ACTION.LIST
        ? 'List'
        : 'Grant';
  const suffix = contextLabel ? ` (${contextLabel})` : '';
  return `${verb} ${mobileNumber}${suffix}`;
}

export function createKeyRequestService(dataverseApiCall) {
  const getStatus = async (requestId, { includeResult = false } = {}) => {
    const select = includeResult ? KEY_REQUEST_SELECT_WITH_RESULT : KEY_REQUEST_SELECT;
    const response = await dataverseApiCall(
      `/oc_keyrequests(${formatDataverseRecordKey(requestId)})?$select=${select}`
    );
    return mapKeyRequest(response);
  };

  return {
    create: async ({
      action,
      scope,
      mobileNumber,
      phoneId,
      durationMinutes = KEY_REQUEST_DURATION_DEFAULT,
      lockId,
      residentId,
      contextLabel
    }) => {
      const normalizedMobile = normalizeMobileNumber(mobileNumber);
      if (!normalizedMobile) {
        throw new Error('Ugyldig mobilnummer. Bruk norsk format, f.eks. +4791750101.');
      }

      const payload = {
        oc_name: buildRequestName({ action, mobileNumber: normalizedMobile, contextLabel }),
        oc_action: action,
        oc_mobilnummer: normalizedMobile,
        oc_status: KEY_REQUEST_STATUS.PENDING
      };

      if (phoneId) {
        payload['oc_Telefon@odata.bind'] = `/oc_telefons(${formatDataverseRecordKey(phoneId)})`;
      }

      if (action === KEY_REQUEST_ACTION.GRANT) {
        payload.oc_scope = scope;
        payload.oc_varighetminutter = durationMinutes;

        if (scope === KEY_REQUEST_SCOPE.SINGLE_LOCK) {
          if (!lockId) {
            throw new Error('Lås må velges for enkelt-nøkkel.');
          }
          payload['oc_Lock@odata.bind'] = `/oc_locks(${formatDataverseRecordKey(lockId)})`;
        } else if (scope === KEY_REQUEST_SCOPE.ALL_LOCKS_FOR_RESIDENT) {
          if (!residentId) {
            throw new Error('Innbygger må velges for nøkler til alle lås.');
          }
          payload['oc_Innbygger@odata.bind'] = `/oc_innbyggers(${formatDataverseRecordKey(residentId)})`;
        } else if (scope === KEY_REQUEST_SCOPE.ALL_LOCKS) {
          // Blålys: no lock/resident binding required.
        }
      } else if (lockId) {
        payload['oc_Lock@odata.bind'] = `/oc_locks(${formatDataverseRecordKey(lockId)})`;
      }

      const response = await dataverseApiCall('/oc_keyrequests', {
        method: 'POST',
        headers: {
          Prefer: 'return=representation'
        },
        body: JSON.stringify(payload)
      });

      const recordId = response?.oc_keyrequestid;
      if (!recordId) {
        throw new Error('Kunne ikke opprette nøkkelforespørsel i Dataverse.');
      }

      return mapKeyRequest(response);
    },

    listManual: async ({ top = 200 } = {}) => {
      const filter = `(oc_action eq ${KEY_REQUEST_ACTION.GRANT} or oc_action eq ${KEY_REQUEST_ACTION.REVOKE})`;
      const query =
        `/oc_keyrequests?$select=${KEY_REQUEST_SELECT},_createdby_value` +
        `&$filter=${filter}&$orderby=createdon desc&$top=${top}`;
      const headers = {
        Prefer: 'odata.include-annotations="OData.Community.Display.V1.FormattedValue"'
      };

      try {
        const response = await dataverseApiCall(`${query}&$expand=createdby($select=fullname)`, {
          headers
        });
        return (response?.value || []).map(mapKeyRequest);
      } catch {
        const response = await dataverseApiCall(query, { headers });
        return (response?.value || []).map(mapKeyRequest);
      }
    },

    listByMobile: async (mobileNumber) => {
      const normalized = normalizeMobileNumber(mobileNumber);
      if (!normalized) {
        return [];
      }

      const escaped = normalized.replace(/'/g, "''");
      const response = await dataverseApiCall(
        `/oc_keyrequests?$select=${KEY_REQUEST_SELECT}&$filter=oc_mobilnummer eq '${escaped}'&$orderby=createdon asc&$top=200`
      );
      return (response.value || []).map(mapKeyRequest);
    },

    listFromUnloc: async ({ mobileNumber, phoneId, contextLabel }) => {
      const service = createKeyRequestService(dataverseApiCall);
      const created = await service.create({
        action: KEY_REQUEST_ACTION.LIST,
        mobileNumber,
        phoneId,
        contextLabel
      });
      await service.waitForCompletion(created.requestId);
      return getStatus(created.requestId, { includeResult: true });
    },

    getStatus,

    waitForCompletion: async (requestId, { intervalMs = 2500, timeoutMs = 30000 } = {}) => {
      const started = Date.now();

      while (Date.now() - started < timeoutMs) {
        const status = await getStatus(requestId);

        if (status.status === KEY_REQUEST_STATUS.COMPLETED) {
          return status;
        }

        if (status.status === KEY_REQUEST_STATUS.FAILED) {
          const message = status.errorMessage || 'Nøkkelforespørsel feilet.';
          throw new Error(message);
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }

      throw new Error('Tidsavbrudd: nøkkelforespørsel ble ikke ferdig behandlet i tide.');
    }
  };
}
